import {
  Box,
  Modal,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  useDisclosure,
  useToken,
} from '@chakra-ui/react';
import {
  Button,
  CloudinaryGif,
  CloudinaryVideo,
  WrappedImage,
} from '@time-line/components/common';
import { useMultipleViewportCrossing } from '@time-line/components/hooks';
import type { SanityImage } from '@time-line/components/types';
import { ratioToDecimal } from '@time-line/components/utils';
import { useState } from 'react';
import type { ProductMedia } from 'types/cms/pages/ProductPage';

import {
  PRODUCT_GALLERY_RATIO,
  PRODUCT_GALLERY_RATIO_DECIMAL,
} from '../constants';
import type { ProductGalleryProps } from '../types';
import { ProductGalleryDesktopItemWrapper as ItemWrapper } from './ProductGalleryDesktopItemWrapper';

export function ProductGalleryDesktop({
  media,
  isDoctorRecommended,
  isLoading = false,
  discountBadge,
}: ProductGalleryProps) {
  const {
    isOpen: isModalOpen,
    onOpen: openModal,
    onClose: closeModal,
  } = useDisclosure();
  const [activeIndex, setActiveIndex] = useState(0);
  /* setActiveIndex is only on images */
  const activeMedia = media[activeIndex] as SanityImage;
  const sm = useToken('space', 'sm');
  const aspectRatio = ratioToDecimal(PRODUCT_GALLERY_RATIO);

  /* INFO: The video index is a separate counter to images to keep the indices
   * matched up with useMultipleViewportCrossing.
   * ESLint doesn't like '++', hence getVideoIndex(). */
  const [activeVideoIndex, setPlayingVideoIndex] = useState<number | null>(0);
  const getVideoIndex = (() => {
    let index = 0;
    return () => {
      const currentIndex = index;
      index += 1;
      return currentIndex;
    };
  })();
  const numVideos = media.filter(({ _type }) => _type === 'video').length;
  const [intersectionRefs, { hasPassed }] =
    useMultipleViewportCrossing<HTMLVideoElement>(numVideos);

  const handleThumbnailClick = (index: number) => {
    setActiveIndex(index);
    openModal();
  };

  return (
    <Box>
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          maxW={600}
          position="relative"
          maxH={`calc(100vh - (2 * ${sm}))`}
          marginTop={0}
          paddingX={0}
          overflowY="auto"
        >
          <WrappedImage
            image={activeMedia}
            sizes="(min-width: 960px) 50vw"
            wrapperProps={{ width: '100%' }}
            ratio={aspectRatio}
          />
        </ModalContent>
      </Modal>

      <SimpleGrid columns={2} display="grid" gap="4xs">
        {media.map((item, i) => {
          const ThumbnailElement = i === 0 ? Box : Button;
          const isFirstThumbnail = i === 0;

          const getGalleryItem = (galleryItem: ProductMedia) => {
            switch (galleryItem._type) {
              case 'video': {
                const videoIdx = getVideoIndex();
                const canPlay =
                  activeVideoIndex === videoIdx || hasPassed[videoIdx];

                return (
                  <CloudinaryVideo
                    ref={intersectionRefs[videoIdx]}
                    aspectRatio={PRODUCT_GALLERY_RATIO}
                    cloudinaryId={galleryItem.cloudinaryId}
                    poster={galleryItem.poster}
                    title={galleryItem.title}
                    canPlay={canPlay}
                    onPlaying={() => setPlayingVideoIndex(videoIdx)}
                    hasAudio={galleryItem.hasAudio}
                  />
                );
              }
              case 'image':
                return (
                  <ThumbnailElement
                    onClick={() => handleThumbnailClick(i)}
                    display="block"
                    variant="unstyled"
                    height="100%"
                    width="100%"
                    cursor="zoom-in"
                  >
                    <WrappedImage
                      image={galleryItem}
                      sizes="(min-width: 1920px) 948px, (min-width: 960px) 50vw, 87vw"
                      priority={isFirstThumbnail}
                      ratio={PRODUCT_GALLERY_RATIO_DECIMAL}
                    />
                  </ThumbnailElement>
                );
              case 'gif':
                return (
                  <CloudinaryGif
                    cloudinaryId={galleryItem.cloudinaryId}
                    title={galleryItem.title}
                    objectFit="cover"
                    width="100%"
                    height="100%"
                  />
                );
              default:
                return null;
            }
          };

          return (
            <ItemWrapper
              key={i}
              ratio={PRODUCT_GALLERY_RATIO_DECIMAL}
              isDoctorRecommended={
                !isLoading && isFirstThumbnail && isDoctorRecommended
              }
              discountBadge={
                !isLoading && isFirstThumbnail ? discountBadge : null
              }
            >
              {isLoading ? <Skeleton /> : getGalleryItem(item)}
            </ItemWrapper>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}
